import { defineStore } from 'pinia';
import {
  activityLayout,
  markerY,
} from '@/app/remap/canvas/layout/activityLayout';
import { useIoStore } from '@/store/ioStore';
import { usePresenceStore } from '@/store/presenceStore';
import { useMapStore } from '@/store/remapStore';
import {
  addAiGhost,
  addAiGlow,
  HIGHLIGHT_GLOW_MS,
  type AiGhost,
} from './liveEffects';
import { LiveSync } from './liveSync';
import {
  liveKey,
  mapTargetOf,
  mapTargetsOf,
  removedTargetOf,
  rowIdOf,
  type AiLiveMode,
  type AiMapTarget,
} from './liveTargets';
import type { AiStep } from '@beeboard/common';

/**
 * The AI assistant at work on the page, like a colleague (WI #5767, D4/D5).
 * While an answer runs, each entity it saves is refreshed in the stores at
 * once (`LiveSync`); the step it saved last gets the AI's ring on the map,
 * glows, and is kept in view; list rows and kanban cards flash. All of it is
 * local to the tab that runs the answer — colleagues are slice 5 (#5770).
 *
 * `aiAssistantStore` drives it: `runStarted` / `stepSaved` / `runEnded`.
 */

/**
 * The rate rule (D5): when steps come faster, the ring, the glow and the
 * follow move at most once per interval — always to the latest step.
 */
export const PRESENT_INTERVAL_MS = 1000;
/** How long a list row or kanban card flashes. */
export const FLASH_MS = 1600;
/** The pause that lets a row flashing already start its flash over. */
export const FLASH_RESTART_MS = 40;
/** How long the reload at the end of an answer waits for the live refresh. */
export const SETTLE_TIMEOUT_MS = 3000;

/** What the page shows of the step the AI saved last. */
export interface AiLivePresentation {
  /** Counts up with every step shown, so a watcher sees a repeat too. */
  seq: number;
  /** The map entity to ring, glow and keep in view. */
  target: AiMapTarget | null;
  /** The list row / kanban card to flash and scroll into view. */
  rowId: string | null;
}

interface AiLiveRun {
  /** Where the AI's ring sits: the entity it saved last. */
  ring: AiMapTarget | null;
  /** The map follows the AI; a manual pan, zoom or selection ends it. */
  following: boolean;
  presentation: AiLivePresentation | null;
}

/** "Auf Karte zeigen", waiting for the map of its project / template. */
export interface AiLiveHighlight {
  key: string;
  targets: AiMapTarget[];
  seq: number;
}

type AiLiveStoreState = {
  runs: Record<string, AiLiveRun>;
  /** List rows / kanban cards flashing right now, by entity id. */
  flashing: Record<string, true>;
  /** Bumps whenever a glow or a fade starts, so the map repaints. */
  effectsVersion: number;
  highlight: AiLiveHighlight | null;
};

type GhostPlace = Pick<AiGhost, 'type' | 'pos' | 'y'>;

/** Per run, outside the reactive state. */
interface RunHelpers {
  sync: LiveSync;
  lastShownAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  pending: AiStep | null;
  /** Where removed entities were, by step — read before they vanish. */
  ghosts: Map<string, GhostPlace>;
}

const helpers = new Map<string, RunHelpers>();
const flashTimers = new Map<string, ReturnType<typeof setTimeout>>();
let highlightSeq = 0;

/** A step, also among those of a run the user left in the same chat. */
function stepKey(step: AiStep): string {
  return `${step.tool_call_id}:${step.seq}`;
}

function disposeHelpers(key: string) {
  const run = helpers.get(key);
  if (run) {
    run.sync.dispose();
    if (run.timer) {
      clearTimeout(run.timer);
    }
    helpers.delete(key);
  }
}

/** Where a removed entity sits on the shown map, while it is still there. */
function placeOfRemoved(
  mode: AiLiveMode,
  contextId: string,
  step: AiStep,
): GhostPlace | null {
  const removed = removedTargetOf(step);
  const map = useMapStore();
  if (
    !removed ||
    map.mapId !== contextId ||
    map.isTemplate !== (mode === 'template')
  ) {
    return null;
  }
  try {
    const io = useIoStore();
    if (removed.type === 'milestone') {
      const milestone = io.milestones.find(({ id }) => id === removed.id);

      return milestone && !milestone.isHiddenOnMap
        ? {
            type: 'milestone',
            pos: map.milestonePos(milestone),
            y: milestone.mirrored ? 148 : 128,
          }
        : null;
    }
    const activity = io.activities.find(({ id }) => id === removed.id);
    if (!activity || activity.isHiddenOnMap) {
      return null;
    }
    // Where the canvas draws it: the layout engine's Y, or the marker of the
    // collapsed group it hides behind.
    const marker = activityLayout.markerOf(activity.id);

    return marker
      ? {
          type: 'activity',
          pos: marker.pos,
          y: markerY(marker, (id) => activityLayout.yOf(id, map)),
        }
      : {
          type: 'activity',
          pos: map.activityPos(activity),
          y: activityLayout.yOf(activity.id, map),
        };
  } catch {
    // The map is not ready: nothing to fade out.
    return null;
  }
}

export const useAiLiveStore = defineStore('aiLiveStore', {
  state: (): AiLiveStoreState => ({
    runs: {},
    flashing: {},
    effectsVersion: 0,
    highlight: null,
  }),

  getters: {
    /** An answer of this project / template runs in this tab. */
    isActive:
      (state) =>
      (mode: AiLiveMode, contextId: string): boolean =>
        !!state.runs[liveKey(mode, contextId)],

    ringFor:
      (state) =>
      (mode: AiLiveMode, contextId: string): AiMapTarget | null =>
        state.runs[liveKey(mode, contextId)]?.ring ?? null,

    isFollowing:
      (state) =>
      (mode: AiLiveMode, contextId: string): boolean =>
        !!state.runs[liveKey(mode, contextId)]?.following,

    presentationFor:
      (state) =>
      (mode: AiLiveMode, contextId: string): AiLivePresentation | null =>
        state.runs[liveKey(mode, contextId)]?.presentation ?? null,

    isFlashing:
      (state) =>
      (id: string): boolean =>
        !!state.flashing[id],
  },

  actions: {
    /**
     * An answer started (or was rejoined). The map follows it from the
     * start, unless the user follows a colleague right now.
     */
    runStarted(mode: AiLiveMode, contextId: string) {
      const key = liveKey(mode, contextId);
      if (!this.runs[key]) {
        // Helpers without their run are stale: they go with it.
        disposeHelpers(key);
        this.runs[key] = {
          ring: null,
          following: !usePresenceStore().isFollowing,
          presentation: null,
        };
      }
      if (!helpers.has(key)) {
        helpers.set(key, {
          sync: new LiveSync(mode, contextId, (steps) =>
            this.applied(key, steps),
          ),
          lastShownAt: Number.NEGATIVE_INFINITY,
          timer: null,
          pending: null,
          ghosts: new Map(),
        });
      }
    },

    /** The answer saved (or failed on) an entity: show it on the page. */
    stepSaved(mode: AiLiveMode, contextId: string, step: AiStep) {
      this.runStarted(mode, contextId);
      const run = helpers.get(liveKey(mode, contextId));
      if (!run) {
        return;
      }
      const place = placeOfRemoved(mode, contextId, step);
      if (place) {
        run.ghosts.set(stepKey(step), place);
      }
      run.sync.add(step);
    },

    /**
     * The last answer of this project / template ended — done, failed or
     * stopped: the ring goes and the map stops following. Its reload at the
     * end takes over from the steps still waiting.
     */
    runEnded(mode: AiLiveMode, contextId: string) {
      const key = liveKey(mode, contextId);
      disposeHelpers(key);
      delete this.runs[key];
    },

    /** The page shows a batch of steps: fade out, then present the latest. */
    applied(key: string, steps: AiStep[]) {
      const run = helpers.get(key);
      if (!run || !this.runs[key]) {
        return;
      }
      let faded = false;
      for (const step of steps) {
        const place = run.ghosts.get(stepKey(step));
        if (place) {
          run.ghosts.delete(stepKey(step));
          addAiGhost(key, place);
          faded = true;
        }
      }
      if (faded) {
        this.effectsVersion++;
      }

      const latest = [...steps]
        .reverse()
        .find((step) => mapTargetOf(step) || rowIdOf(step));
      if (!latest) {
        return;
      }
      const wait = run.lastShownAt + PRESENT_INTERVAL_MS - Date.now();
      if (wait <= 0 && !run.timer) {
        this.present(key, latest);

        return;
      }
      run.pending = latest;
      if (!run.timer) {
        run.timer = setTimeout(
          () => {
            run.timer = null;
            const next = run.pending;
            run.pending = null;
            if (next) {
              this.present(key, next);
            }
          },
          Math.max(0, wait),
        );
      }
    },

    present(key: string, step: AiStep) {
      const live = this.runs[key];
      const run = helpers.get(key);
      if (!live || !run) {
        return;
      }
      run.lastShownAt = Date.now();
      const target = mapTargetOf(step);
      const rowId = rowIdOf(step);
      if (target) {
        live.ring = target;
        addAiGlow(key, target);
        this.effectsVersion++;
      }
      if (rowId) {
        this.flash(rowId);
      }
      live.presentation = {
        seq: (live.presentation?.seq ?? 0) + 1,
        target,
        rowId,
      };
    },

    /**
     * Flashes a list row / kanban card. One that flashes already starts
     * over: its class goes for a moment, so the animation plays again.
     */
    flash(id: string) {
      const timer = flashTimers.get(id);
      if (timer) {
        clearTimeout(timer);
      }
      const begin = () => {
        this.flashing[id] = true;
        flashTimers.set(
          id,
          setTimeout(() => {
            flashTimers.delete(id);
            delete this.flashing[id];
          }, FLASH_MS),
        );
      };
      if (this.flashing[id]) {
        delete this.flashing[id];
        flashTimers.set(id, setTimeout(begin, FLASH_RESTART_MS));
      } else {
        begin();
      }
    },

    /**
     * Waits (briefly) for the batch the live refresh is applying, so the
     * reload at the end of an answer is not overwritten by a response that
     * was sent before the answer's last change.
     */
    async whenSettled(mode: AiLiveMode, contextId: string): Promise<void> {
      const run = helpers.get(liveKey(mode, contextId));
      if (!run) {
        return;
      }
      let timer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        run.sync.idle(),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, SETTLE_TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timer);
    },

    /** "Ansicht folgen" on the AI's avatar. */
    follow(mode: AiLiveMode, contextId: string) {
      const run = this.runs[liveKey(mode, contextId)];
      if (!run) {
        return;
      }
      // One view to follow at a time.
      const presence = usePresenceStore();
      if (presence.isFollowing) {
        presence.unfollowUser();
      }
      run.following = true;
    },

    /** A manual pan, zoom or selection, or "Nicht mehr folgen". */
    stopFollowing(mode: AiLiveMode, contextId: string) {
      const run = this.runs[liveKey(mode, contextId)];
      if (run) {
        run.following = false;
      }
    },

    /**
     * "Auf Karte zeigen" on a finished answer: its entities wait for the
     * map of their project / template, which may still have to open.
     * False when the answer changed nothing the map shows.
     */
    showOnMap(
      mode: AiLiveMode,
      contextId: string,
      steps: ReadonlyArray<AiStep> | undefined,
    ): boolean {
      const targets = mapTargetsOf(steps);
      if (!targets.length) {
        return false;
      }
      this.highlight = {
        key: liveKey(mode, contextId),
        targets,
        seq: ++highlightSeq,
      };

      return true;
    },

    /** "Auf Karte zeigen" whose map did not open: nothing waits for it. */
    dropHighlight() {
      this.highlight = null;
    },

    /** The map takes the highlight waiting for it; its entities glow. */
    takeHighlight(mode: AiLiveMode, contextId: string): AiMapTarget[] | null {
      const key = liveKey(mode, contextId);
      if (this.highlight?.key !== key) {
        return null;
      }
      const { targets } = this.highlight;
      this.highlight = null;
      for (const target of targets) {
        addAiGlow(key, target, HIGHLIGHT_GLOW_MS);
      }
      this.effectsVersion++;

      return targets;
    },
  },
});
